import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { getLogService } from "@cervval.js/common/src/services/log.service";
import { anyObject, setProp } from "@cervval.js/common/src/tools/any.tools";
import { googleReCaptchaInjectedKey, reCAPTCHAAction } from "@ehealth_tools/common-google-recaptcha/src/googleReCaptcha.constants";

/** Unique identifier to use an AbstractClientGoogleReCaptchaService or its descendent for injection */
export const CLIENT_GOOGLE_RECAPTCHA_SERVICE = "CLIENT_GOOGLE_RECAPTCHA_SERVICE";

/** Abstract class providing a function to encapsulate a route call with a reCAPTCHA verification */
@Injectable(CLIENT_GOOGLE_RECAPTCHA_SERVICE)
export abstract class AbstractClientGoogleReCaptchaService {

    /** Get the application public token, needed to use Google ReCAPTCHA v3 API. */
    protected abstract getGoogleReCaptchaPublicKey(): string;

    /** Static flag. True if loading script has been already injected in DOM */
    private static _scriptInjected = false;

    constructor() {
        void this._loadGoogleReCaptchaScriptIfNeeded();
    }

    /**
     * Encapsulate a route call within a reCAPTCHA verification
     * @param routeCall Route to call
     * @param params Params to provide to the route
     * @returns Whatever the route promised to return, if reCAPTCHA verification succeeded.
     * @throws If reCAPTCHA verification failed, or if routeCall throws
     */
    public async encapsulateRouteCallWithCaptcha<RouteReturnType, RouteParamsType extends anyObject>(
        routeCall: (params: RouteParamsType, routeParams?: anyObject) => Promise<RouteReturnType>,
        params: RouteParamsType
    ): Promise<RouteReturnType> {
        try {
            // Check if reCAPTCHA script has been loaded
            if (!this._isGoogleReCaptchaScriptLoaded()) { throw Error("reCAPTCHA script isn't load"); }

            // Retrieve a client token, that will be injected into params object
            // TODO add a way to override action with something we could check on server-side
            await grecaptcha.execute(this.getGoogleReCaptchaPublicKey(), { action: reCAPTCHAAction }).then((token: string) => {
                setProp(params, googleReCaptchaInjectedKey, token);
            });
        } catch (err) {
            getLogService().error("[AbstractClientGoogleReCaptchaService]", "[encapsulateRouteCallWithCaptcha]", err);
            throw Error("Unable to launch reCAPTCHA v3 verification");
        }

        return routeCall(params);
    }

    /**
     * Async function that checks if the Google ReCAPTCHA API script has been loaded, and if not, loads it.
     * @returns 
     */
    private _loadGoogleReCaptchaScriptIfNeeded(): Promise<void> {
        return new Promise((resolve, reject) => {
            // Check if grecaptcha has been loaded
            if (this._isGoogleReCaptchaScriptLoaded()) { resolve(); return; }

            // Script is only injected once
            if (AbstractClientGoogleReCaptchaService._scriptInjected === true) { reject(new Error("grecaptcha isn't loaded, and script has been already injected")); return; }

            // Create a script element to fetch js module for our application
            const loadScript = createHtmlElement("script", {
                attributes: {
                    src: `https://www.google.com/recaptcha/api.js?render=${this.getGoogleReCaptchaPublicKey()}&trustedtypes=true`,
                    async: "true"
                }
            });

            // Handle script loading
            loadScript.onload = () => {
                if (this._isGoogleReCaptchaScriptLoaded()) { resolve(); return; }
                else { reject(new Error("reCaptcha failed to load")); return; }
            };
            // Handle script loading error
            loadScript.onerror = () => { reject(new Error("Failed to load reCaptcha script")); return; };

            // Append script to document and set injection flag
            document.head.appendChild(loadScript);
            AbstractClientGoogleReCaptchaService._scriptInjected = true;
        });
    }

    /** Check if the Google ReCAPTCHA API script has been loaded */
    private _isGoogleReCaptchaScriptLoaded(): boolean { return typeof grecaptcha !== "undefined"; }
}